﻿import { useNavigate, useSearchParams } from "react-router-dom";
import { useAssetDetailsContext } from "@/pages/asset-details/context/assetDetailsContext";
import { useMemo, useRef, useCallback, useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import debounce from "lodash/debounce";
import type {
	EditableCallbackParams,
	GridApi,
	ColDef,
	MenuItemDef,
	DefaultMenuItem,
	GetContextMenuItemsParams,
	RowDoubleClickedEvent,
	SelectionChangedEvent,
} from "ag-grid-community";
import { createStyles, makeStyles } from "@mui/styles";
import {
	TextField,
	IconButton,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	DialogContentText,
	FormControlLabel,
	Switch,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import AddBoxIcon from "@mui/icons-material/AddBox";
import SearchIcon from "@mui/icons-material/Search";
import FormatListBulletedSharpIcon from "@mui/icons-material/FormatListBulletedSharp";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";
import { PropertiesModal } from "./components/properties-modal/properties-modal";
import { AttachmentsCommentsColumn } from "./components/comments-buttons/comments-buttons.columns";
import { AttachmentNameColumn } from "./components/editing-row/attachment-name.column";
import { AttachmentAssetNameColumn } from "./components/editing-row/attachment-AssetName.column";
import { AttachmentsDownloadButtons } from "./components/download-buttons/attachment-download-buttons.columns";
import { useAssetAttachmentsGridActionsAndButtons } from "./hooks/useAssetAttachmentsGridActionsAndButtons";
import CustomLoadingOverlay from "../../../../../components/loaders/LazyLoading/LazyLoading";
import { UploadFilePage } from "./components/uploadFile-dialog/uploadFilePage";
import { useSearchAssetAttachments } from "./hooks/useSearchAssetAttachments";
import { useSearchAttachments } from "./hooks/useSearchAttachments";
import { ErrorModal, useToken } from "@dexteel/mesf-core";
import {
	addLinkAssetAttachments,
	removeAttachmentLink,
} from "@/pages/asset-details/repositories/AssetDetailsRepositorie";
import { AssetAttachment } from "@/pages/asset-details/models/AssetAttachment";

const useStyles = makeStyles((theme) =>
	createStyles({
		agGridContainer: {
			position: "relative",
		},
		searchToolbar: {
			display: "flex",
			alignItems: "center",
			gap: theme.spacing(1),
			padding: "8px 0px",
		},
		searchField: {
			flex: 1,
			marginRight: 0,
			"& .MuiOutlinedInput-root": {
				height: "40px",
			},
		},
		addButton: {
			minWidth: "40px",
			height: "40px",
			padding: 0,
		},
		gridWrapper: {
			height: "calc(100% - 60px)",
		},
	}),
);

type Props = {
	searchStatistics: Function;
};

export const AssetAttachmentsGrid = ({ searchStatistics }: Props) => {
	const navigate = useNavigate();
	const classes = useStyles();
	const [openPropertiesDialog, setOpenPropertiesDialog] =
		useState<boolean>(false);
	const [searchParams] = useSearchParams();
	const resource = searchParams.get("resource") ?? "";
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState("");
	const [isAssetAttachmentsLoading, setIsAssetAttachmentsLoading] =
		useState<boolean>(false);
	const [assetAttachmentsError, setAssetAttachmentsError] =
		useState<string>("");
	const [isLinkAssetAttachmentsLoading, setIsLinkAssetAttachmentsLoading] =
		useState<boolean>(true);
	const [
		linkAssetAttachmentsLoadingError,
		setLinkAssetAttachmentsLoadingError,
	] = useState<string>("");
	const [
		isRemoveLinkAssetAttachmentsLoading,
		setIsRemoveLinkAssetAttachmentsLoading,
	] = useState<boolean>(true);
	const [
		removeLinkAssetAttachmentsLoadingError,
		setRemoveLinkAssetAttachmentsLoadingError,
	] = useState<string>("");
	const [quickFilterText, setQuickFilterText] = useState("");
	const [includeDescendants, setIncludeDescendants] = useState(true);
	const [listAttachmentSelected, setListAttachmentSelected] = useState<any[]>(
		[],
	);
	const [deleteOpen, setDeleteOpen] = useState(false);

	const { token: bearerToken } = useToken();

	const searchAttachments = useSearchAttachments({ setIsLoading, setError });
	const searchAssetAttachments = useSearchAssetAttachments({
		setIsAssetAttachmentsLoading,
		setAssetAttachmentsError,
	});

	const {
		state: {
			assetSearchId,
			listAssetAttachments,
			attachmentSearch,
			rowSelected,
			currentGridName,
			uploadFileDialog,
		},
		actions: {
			setFirstTime,
			setAttachmentSearch,
			setOpenAddCommentDialog,
			setLoaderListSearchAttachments,
			setUploadFileDialog,
			setDisplayViewerAttachment,
			setRowSelected,
		},
	} = useAssetDetailsContext();

	const debouncedSearch = useRef(
		debounce((attachmentSearch?: string) => {
			searchAttachments(attachmentSearch || undefined).then(() => {
				setLoaderListSearchAttachments(false);
			});
		}, 3000),
	).current;

	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const containerStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
	const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
	const [columnDefs, setColumnDefs] = useState<ColDef[]>();

	const onActionsClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		params: EditableCallbackParams,
	) => {
		(params.api as any).showContextMenu({
			column: params.column,
			rowNode: params.node,
			value: undefined,
			mouseEvent: e.nativeEvent,
		});
	};

	const {
		refresh,
		removeSelected,
		deleteButton,
		saveButton,
		setAddButton,
		setDeleteButton,
		setSaveButton,
		setDiscardButton,
	} = useAssetAttachmentsGridActionsAndButtons({
		gridRef: gridApi,
		searchAssetAttachments,
		setIsRemoveLinkAssetAttachmentsLoading,
		setRemoveLinkAssetAttachmentsLoadingError,
		includeDescendants,
	});

	const openUploadFileDialogHandler = useCallback(() => {
		searchAttachments();
		setUploadFileDialog(true);
	}, [searchAttachments, setUploadFileDialog]);

	const handleQuickFilterChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const value = event.target.value;
			setQuickFilterText(value);
			if (gridApi) {
				gridApi.setGridOption("quickFilterText", value);
			}
		},
		[gridApi],
	);

	const onRowDoubleClicked = useCallback(
		(event: RowDoubleClickedEvent) => {
			if (event.data) {
				setRowSelected(event.data);
				if (gridApi) {
					event.node.setSelected(true);
				}
				setOpenPropertiesDialog(true);
			}
		},
		[gridApi, setRowSelected],
	);

	const onSelectionChanged = useCallback((event: SelectionChangedEvent) => {
		const rowCount = event.api.getSelectedNodes().length;
		const listAttachmentsSelected = event.api.getSelectedNodes();
		setListAttachmentSelected(listAttachmentsSelected);
	}, []);

	const renderColumns = () => {
		setColumnDefs([]);
		const newColumns: ColDef[] = [];

		newColumns.push({
			field: "",
			flex: 1,
			minWidth: 30,
			maxWidth: 30,
			filter: false,
			checkboxSelection: true,
			headerCheckboxSelection: true,
			headerCheckboxSelectionFilteredOnly: true,
			getQuickFilterText: () => "",
		});

		newColumns.push({
			cellRenderer: AttachmentNameColumn,
			type: "columnProperties",
			field: "FileName",
			headerName: "Name",
			flex: 2,
			minWidth: 150,
			getQuickFilterText: (params) => params.data?.FileName || "",
		});

		newColumns.push({
			cellRenderer: AttachmentAssetNameColumn,
			type: "columnProperties",
			field: "AssetName",
			headerName: "Asset",
			flex: 2,
			minWidth: 220,
			maxWidth: 220,
			getQuickFilterText: (params) => params.data?.AssetName || "",
		});

		newColumns.push({
			cellRenderer: (params: any) => {
				const dateTime = params.data?.CreationTime;
				if (dateTime) {
					return new Date(dateTime).toLocaleDateString();
				}
				return "";
			},
			type: "columnProperties",
			field: "CreationTime",
			headerName: "Upload Datetime",
			flex: 1,
			minWidth: 120,
			maxWidth: 120,
			getQuickFilterText: (params) => {
				const dateTime = params.data?.CreationTime;
				if (dateTime) {
					const fullDate = new Date(dateTime).toString();
					const shortDate = new Date(dateTime).toLocaleDateString();
					return `${fullDate} ${shortDate}`;
				}
				return "";
			},
		});

		newColumns.push({
			cellRenderer: (params: any) => params.data?.LinkedBy || "",
			type: "columnProperties",
			field: "LinkedBy",
			headerName: "Uploader",
			flex: 1,
			minWidth: 100,
			maxWidth: 100,
			getQuickFilterText: (params) => params.data?.LinkedBy || "",
		});

		newColumns.push({
			cellRenderer: (params: any) =>
				AttachmentsCommentsColumn({
					data: params?.data,
					searchAssetAttachments,
				}),
			field: "Comments",
			headerName: "Comments",
			flex: 1,
			type: "dataName",
			minWidth: 90,
			maxWidth: 90,
			getQuickFilterText: (params) => params.data?.Comments || "",
		});

		newColumns.push({
			cellRenderer: AttachmentsDownloadButtons,
			field: "Download",
			headerName: "",
			flex: 1,
			minWidth: 120,
			maxWidth: 120,
			editable: false,
			autoHeight: true,
			suppressNavigable: true,
		});

		newColumns.push({
			headerName: "",
			flex: 1,
			minWidth: 65,
			maxWidth: 65,
			cellClass: "ag-columns",
			wrapText: false,
			autoHeight: false,
			cellRenderer: (params: EditableCallbackParams) => {
				return (
					<Grid2
						container
						style={{
							height: "100%",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<Grid2
							style={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
							size={{ xs: 12, md: 12 }}
						>
							<Grid2
								container
								spacing={1}
								style={{ justifyContent: "flex-end", minWidth: 30 }}
							>
								<Grid2 size={{ xs: 12, md: 12 }}>
									<Button
										fullWidth
										style={{}}
										onClick={(e) => onActionsClick(e, params)}
									>
										<FormatListBulletedSharpIcon
											style={{ height: "auto" }}
											color="action"
										/>
									</Button>
								</Grid2>
							</Grid2>
						</Grid2>
					</Grid2>
				);
			},
		});

		setColumnDefs(newColumns);
	};

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			if (gridApi && params.node) {
				const selectedNodes = gridApi.getSelectedNodes();
				const isCurrentlySelected = params.node.isSelected();

				if (selectedNodes.length > 1 && isCurrentlySelected) {
				} else if (!isCurrentlySelected) {
					gridApi.deselectAll();
					params.node.setSelected(true);
				}
			}

			setRowSelected(params.node?.data);
			const rowSelectedInGrid = params.node?.data;
			const selectedAttachment = gridApi?.getSelectedNodes();

			const storedAttachments = localStorage.getItem("attachmentCopied");
			let AttachmentsToUpdate: AssetAttachment[] = [];
			try {
				if (storedAttachments) {
					AttachmentsToUpdate = JSON.parse(storedAttachments);
				}
			} catch (e) {
				console.error("Error parsing attachmentCopied from localStorage", e);
				localStorage.removeItem("attachmentCopied");
			}

			const AttachmentsCopiedInLocalStorage = () => {
				if (selectedAttachment?.length) {
					const selectedAttachmentsId = selectedAttachment.map(
						(rowNode) => rowNode.data,
					);
					localStorage.setItem(
						"attachmentCopied",
						JSON.stringify(selectedAttachmentsId),
					);
				}
			};

			const PasteAttachmentsCopiedInLocalStorage = async () => {
				try {
					let uploadFileIds: number[] = [];

					// Collect all UploadFileIds
					if (Array.isArray(AttachmentsToUpdate)) {
						uploadFileIds = AttachmentsToUpdate.map(
							(item) => item.UploadFileId,
						).filter((id) => id);
					}

					if (uploadFileIds.length === 0) {
						setError("No attachments to paste");
						return;
					}

					setIsLinkAssetAttachmentsLoading(true);

					// Single bulk API call
					const bulkResult = await addLinkAssetAttachments(
						assetSearchId as string,
						uploadFileIds, // Pass array directly, function will handle it
						"",
					);

					setIsLinkAssetAttachmentsLoading(false);

					if (!bulkResult.ok) {
						setLinkAssetAttachmentsLoadingError(bulkResult.message);
						return;
					}

					// Parse results
					const result = bulkResult.data?.tables?.[0]?.rows?.[0];
					if (result) {
						const newlyLinkedCount = result.NewlyLinkedCount || 0;
						const alreadyLinkedCount = result.AlreadyLinkedCount || 0;
						const alreadyLinkedAttachments = result.AlreadyLinkedAttachments;

						// Show summary message
						let message = `Successfully processed ${uploadFileIds.length} attachments:\n`;
						message += `• ${newlyLinkedCount} newly linked\n`;
						if (alreadyLinkedCount > 0) {
							message += `• ${alreadyLinkedCount} already linked: ${alreadyLinkedAttachments}`;
						}

						if (alreadyLinkedCount > 0) {
							setError(message); // Show as warning for user awareness
						}
					}

					refresh();
				} catch (error) {
					console.error("Error pasting attachments:", error);
					setError("Error pasting attachments");
					setIsLinkAssetAttachmentsLoading(false);
				}
			};

			const DownloaAttachment = () => {
				const link = document.createElement("a");
				link.href = `/backend/files/${params.node?.data.UploadFileId}/download?Authorization=${bearerToken}`;
				link.setAttribute("target", `_blank}`);
				link.setAttribute(
					"download",
					`${params.node?.data.FileName.replace(/\s+/g, "")}`,
				);
				document.body.appendChild(link);
				link.click();
			};

			const result: (DefaultMenuItem | MenuItemDef)[] = [];

			if (params.node?.data && (selectedAttachment?.length ?? 0) <= 1) {
				result.push({
					name: "Properties",
					action: () => {
						setOpenPropertiesDialog(true);
					},
					icon: '<i class="fas fa-file-invoice"></i>',
				});
			}

			if (rowSelectedInGrid && (selectedAttachment?.length ?? 0) <= 1) {
				result.push({
					name: "Go to Asset",
					action: () => {
						setFirstTime(true);
						setSaveButton(false);
						setDiscardButton(false);
						setAddButton(true);
						setDeleteButton(false);
						setDeleteOpen(false);
						setAttachmentSearch("");
						setTimeout(() => {
							navigate(
								`/asset-details/${params.node?.data.AssetId}?resource=${currentGridName}&assets=true&statistics=true&workOrders=true`,
								{ replace: true },
							);
							setIsAssetAttachmentsLoading(false);
						}, 100);
					},
					icon: '<i class="fas fa-external-link-alt"></i>',
				});
			}

			if (resource === "attachments") {
				if (selectedAttachment?.length || rowSelectedInGrid) {
					result.push({
						name:
							(selectedAttachment?.length ?? 0) > 1
								? "Copy Attachments"
								: "Copy Attachment",
						action: AttachmentsCopiedInLocalStorage,
						icon: '<i class="far fa-copy"></i>',
					});
				}

				if (
					AttachmentsToUpdate?.length > 0 &&
					(selectedAttachment?.length ?? 0) <= 1
				) {
					result.push({
						name: "Paste Attachment",
						action: PasteAttachmentsCopiedInLocalStorage,
						icon: '<i class="fas fa-paste"></i>',
					});
				}
			}

			if ((selectedAttachment?.length ?? 0) <= 1) {
				result.push({
					name: "Add Attachment",
					action: () => setUploadFileDialog(true),
					icon: '<i class="fas fa-plus"></i>',
				});
			}

			if (selectedAttachment?.length) {
				// An attachment can only be unlinked from the asset it belongs to; disable Remove
				// (with an explaining tooltip) when any selected attachment is linked to another
				// asset, so the user never triggers the cross-asset unlink error.
				const allOwned = selectedAttachment.every(
					(n: any) => Number(n.data?.AssetId) === Number(assetSearchId),
				);
				result.push({
					name:
						(selectedAttachment?.length ?? 0) > 1
							? "Remove Links"
							: "Remove Link",
					disabled: !allOwned,
					tooltip: allOwned
						? undefined
						: 'Go to the attachment\'s asset ("Go to Asset") to remove it',
					action: () => {
						if (!allOwned) return;
						setListAttachmentSelected(selectedAttachment);
						setDeleteOpen(true);
					},
					icon: '<i class="fas fa-trash"></i>',
				});
			}

			if (!selectedAttachment?.length && rowSelectedInGrid) {
				const owned =
					Number(rowSelectedInGrid.AssetId) === Number(assetSearchId);
				result.push({
					name: "Remove Link",
					disabled: !owned,
					tooltip: owned
						? undefined
						: 'Go to the attachment\'s asset ("Go to Asset") to remove it',
					action: () => {
						if (!owned) return;
						setListAttachmentSelected([]);
						setDeleteOpen(true);
					},
					icon: '<i class="fas fa-trash"></i>',
				});
			}

			if (params.node?.data && (selectedAttachment?.length ?? 0) <= 1) {
				result.push({
					name: "Download",
					action: DownloaAttachment,
					icon: '<i class="fas fa-download"></i>',
				});
			}

			return result;
		},
		[
			listAssetAttachments,
			saveButton,
			openUploadFileDialogHandler,
			assetSearchId,
			gridApi,
			rowSelected,
			searchParams,
			refresh,
			resource,
		],
	);

	const loadingOverlayComponent = useMemo(() => CustomLoadingOverlay, []);

	useEffect(() => {
		// Only search if we have a valid assetSearchId (not 0, undefined, or null)
		// This prevents the initial call with assetSearchId=0 when the component first mounts
		if (resource === "attachments" && assetSearchId && assetSearchId !== 0) {
			searchAssetAttachments(includeDescendants);
		} else if (resource !== "attachments") {
			localStorage.removeItem("attachmentCopied");
		}
	}, [resource, assetSearchId, includeDescendants]);

	useEffect(() => {
		renderColumns();
	}, []);

	return (
		<>
			<div style={containerStyle}>
				<Grid2 container style={gridStyle}>
					<Grid2 className={classes.agGridContainer} size={{ md: 12, xs: 12 }}>
						<div className={classes.searchToolbar}>
							<TextField
								className={classes.searchField}
								variant="outlined"
								placeholder="Search attachments..."
								value={quickFilterText}
								onChange={handleQuickFilterChange}
								slotProps={{
									input: {
										startAdornment: (
											<SearchIcon color="action" style={{ marginRight: 8 }} />
										),
									},
								}}
								size="small"
							/>
							<FormControlLabel
								control={
									<Switch
										checked={includeDescendants}
										onChange={(e) => setIncludeDescendants(e.target.checked)}
										color="primary"
									/>
								}
								label="Include descendant assets"
								style={{ marginLeft: 16, marginRight: 16 }}
							/>
							<IconButton
								onClick={openUploadFileDialogHandler}
								aria-label="addAttachments"
								color="primary"
								className={classes.addButton}
							>
								<AddBoxIcon style={{ fontSize: 40, fontWeight: "bold" }} />
							</IconButton>
						</div>

						<div className={classes.gridWrapper}>
							<AgGridReact
								onGridReady={(params) => setGridApi(params.api)}
								rowData={listAssetAttachments}
								columnDefs={columnDefs}
								defaultColDef={globalDefaultColDef}
								headerHeight={32}
								rowSelection="multiple"
								animateRows={true}
								tooltipShowDelay={300}
								getContextMenuItems={getContextMenuItems}
								onSelectionChanged={onSelectionChanged}
								loadingOverlayComponent={loadingOverlayComponent}
								onRowDoubleClicked={onRowDoubleClicked}
								loading={isAssetAttachmentsLoading}
								quickFilterText={quickFilterText}
							/>
						</div>

						<UploadFilePage
							show={uploadFileDialog}
							gridApi={gridApi}
							searchAssetAttachments={searchAssetAttachments}
							setIsRemoveLinkAssetAttachmentsLoading={
								setIsRemoveLinkAssetAttachmentsLoading
							}
							setRemoveLinkAssetAttachmentsLoadingError={
								setRemoveLinkAssetAttachmentsLoadingError
							}
							includeDescendants={includeDescendants}
						/>
					</Grid2>

					<PropertiesModal
						searchAssetAttachments={searchAssetAttachments}
						show={openPropertiesDialog}
						rowSelected={rowSelected as any}
						onHide={(shouldUpdate) => {
							if (shouldUpdate) {
								setOpenPropertiesDialog(false);
								searchAssetAttachments(includeDescendants);
							} else {
								setOpenPropertiesDialog(false);
							}
						}}
					/>

					<Dialog open={deleteOpen} aria-labelledby="responsive-dialog-title">
						<DialogTitle id="responsive-dialog-title">{"WARNING"}</DialogTitle>
						<DialogContent>
							<DialogContentText style={{ padding: 0, margin: 0 }}>
								{listAttachmentSelected?.length === 1 ? (
									<>
										<p style={{ margin: "0" }}>
											UNLINK <b>{listAttachmentSelected[0].data.FileName}</b>{" "}
											FROM ASSET{" "}
											<b>{listAttachmentSelected[0].data.AssetName}?</b>
										</p>
									</>
								) : listAttachmentSelected.length > 1 ? (
									<>
										<p style={{ margin: 0 }}>UNLINK:</p>
										{listAttachmentSelected?.map((attachment) => {
											return (
												<>
													<p style={{ margin: "5px 0" }}>
														<b>• {attachment.data.FileName}, </b>
													</p>
													<p style={{ margin: 0 }}>
														FROM ASSET <b>{attachment.data.AssetName}.</b>
													</p>
												</>
											);
										})}
									</>
								) : rowSelected?.FileName &&
									listAttachmentSelected?.length === 0 ? (
									<>
										<p style={{ margin: "0" }}>
											UNLINK <b>{rowSelected?.FileName}</b> FROM ASSET{" "}
											<b>{rowSelected?.AssetName}?</b>
										</p>
									</>
								) : (
									<>
										<h1>Something went wrong</h1>
									</>
								)}
							</DialogContentText>
						</DialogContent>
						<DialogActions style={{ padding: "16px 24px" }}>
							<Button
								onClick={() => setDeleteOpen(false)}
								autoFocus
								color="inherit"
							>
								CANCEL
							</Button>
							<Button
								onClick={async () => {
									await removeSelected();
									setDeleteOpen(false);
								}}
								autoFocus
								color="secondary"
							>
								UNLINK
							</Button>
						</DialogActions>
					</Dialog>

					<ErrorModal error={error} onHide={() => setError("")} />
					<ErrorModal
						error={assetAttachmentsError}
						onHide={() => setAssetAttachmentsError("")}
					/>
					<ErrorModal
						error={linkAssetAttachmentsLoadingError}
						onHide={() => setLinkAssetAttachmentsLoadingError("")}
					/>
					<ErrorModal
						error={removeLinkAssetAttachmentsLoadingError}
						onHide={() => setRemoveLinkAssetAttachmentsLoadingError("")}
					/>
				</Grid2>
			</div>
		</>
	);
};
